import { GetterTree } from "vuex";
import { AnnotationStateV2, AnnotationObject, CanvasObjectUpdate } from "./state";
import { defineVuexName, MODULES } from "@/store/utils";

const DEFAULT_RESOLUTION = "1024X722";

const getters: GetterTree<AnnotationStateV2, any> = {
  // ==================== MODE ====================

  /**
   * Check if pointer mode is active
   */
  [defineVuexName(MODULES.ANNOTATION_V2.GETTERS.IS_POINTER_MODE)](state: AnnotationStateV2): boolean {
    return state.mode === 1;
  },

  // ==================== CURRENT OBJECTS ====================

  [defineVuexName(MODULES.ANNOTATION_V2.GETTERS.GET_CURRENT_OBJECTS)](state: AnnotationStateV2): AnnotationObject[] {
    return state.objects;
  },

  // ==================== FILTER BY TYPE ====================

  /**
   * Get all paths (pencil drawings)
   */
  [defineVuexName(MODULES.ANNOTATION_V2.GETTERS.GET_PATHS)](_, getters): AnnotationObject[] {
    const currentKey = defineVuexName(MODULES.ANNOTATION_V2.GETTERS.GET_CURRENT_OBJECTS);
    return getters[currentKey].filter((obj: AnnotationObject) => obj.type === "path");
  },

  /**
   * Get all shapes (circles, rectangles, ellipses, triangles)
   */
  [defineVuexName(MODULES.ANNOTATION_V2.GETTERS.GET_SHAPES)](_, getters): AnnotationObject[] {
    const shapeTypes = ["circle", "rect", "ellipse", "triangle"];
    const currentKey = defineVuexName(MODULES.ANNOTATION_V2.GETTERS.GET_CURRENT_OBJECTS);
    return getters[currentKey].filter((obj: AnnotationObject) => shapeTypes.includes(obj.type));
  },

  /**
   * Get all textboxes
   */
  [defineVuexName(MODULES.ANNOTATION_V2.GETTERS.GET_TEXTBOXES)](_, getters): AnnotationObject[] {
    const currentKey = defineVuexName(MODULES.ANNOTATION_V2.GETTERS.GET_CURRENT_OBJECTS);
    return getters[currentKey].filter((obj: AnnotationObject) => obj.type === "textbox" || obj.type === "i-text");
  },

  /**
   * Get all teacher-only objects
   */
  [defineVuexName(MODULES.ANNOTATION_V2.GETTERS.GET_TEACHER_ONLY_OBJECTS)](_, getters): AnnotationObject[] {
    const currentKey = defineVuexName(MODULES.ANNOTATION_V2.GETTERS.GET_CURRENT_OBJECTS);
    return getters[currentKey].filter((obj: AnnotationObject) => obj.isTeacherOnly);
  },

  // ==================== QUERY BY ID/USER ====================

  /**
   * Get a specific object by objectId
   */
  [defineVuexName(MODULES.ANNOTATION_V2.GETTERS.GET_OBJECT_BY_ID)]:
    (_, getters) =>
    (objectId: string): AnnotationObject | undefined => {
      const currentKey = defineVuexName(MODULES.ANNOTATION_V2.GETTERS.GET_CURRENT_OBJECTS);
      return getters[currentKey].find((obj: AnnotationObject) => obj.objectId === objectId);
    },

  /**
   * Get all objects by userId
   */
  [defineVuexName(MODULES.ANNOTATION_V2.GETTERS.GET_OBJECTS_BY_USER)]:
    (_, getters) =>
    (userId: string): AnnotationObject[] => {
      const currentKey = defineVuexName(MODULES.ANNOTATION_V2.GETTERS.GET_CURRENT_OBJECTS);
      return getters[currentKey].filter((obj: AnnotationObject) => obj.userId === userId);
    },

  // ==================== COUNTS ====================

  /**
   * Total count of objects
   */
  [defineVuexName(MODULES.ANNOTATION_V2.GETTERS.OBJECT_COUNT)](_, getters): number {
    const currentKey = defineVuexName(MODULES.ANNOTATION_V2.GETTERS.GET_CURRENT_OBJECTS);
    return getters[currentKey].length;
  },

  /**
   * Check if there are any objects
   */
  [defineVuexName(MODULES.ANNOTATION_V2.GETTERS.HAS_OBJECTS)](_, getters): boolean {
    const currentKey = defineVuexName(MODULES.ANNOTATION_V2.GETTERS.GET_CURRENT_OBJECTS);
    return getters[currentKey].length > 0;
  },

  // ==================== REALTIME DRAWING ====================

  /**
   * Get realtime pencil paths (in-progress drawings)
   */
  [defineVuexName(MODULES.ANNOTATION_V2.GETTERS.GET_REALTIME_PENCIL_PATHS)](state: AnnotationStateV2) {
    return state.realtimeDrawing.pencilPaths;
  },

  /**
   * Get realtime laser paths (pointer)
   */
  [defineVuexName(MODULES.ANNOTATION_V2.GETTERS.GET_REALTIME_LASER_PATHS)](state: AnnotationStateV2) {
    return {
      lines: state.realtimeDrawing.laserPaths,
      isDone: state.realtimeDrawing.isLaserDone,
    };
  },

  // ==================== IMAGE DIMENSIONS ====================

  /**
   * Get lesson plan image width
   */
  [defineVuexName(MODULES.ANNOTATION_V2.GETTERS.GET_IMAGE_WIDTH)](state: AnnotationStateV2): number {
    return state.imgWidth || parseInt(DEFAULT_RESOLUTION.split("X")[0]);
  },

  /**
   * Get lesson plan image height
   */
  [defineVuexName(MODULES.ANNOTATION_V2.GETTERS.GET_IMAGE_HEIGHT)](state: AnnotationStateV2): number {
    return state.imgHeight || parseInt(DEFAULT_RESOLUTION.split("X")[1]);
  },

  // ==================== EXPORT FOR BACKEND ====================

  /**
   * Export objects as JSON strings (for backend compatibility)
   * Groups by type for backward compatibility with old API
   */
  [defineVuexName(MODULES.ANNOTATION_V2.GETTERS.EXPORT_FOR_BACKEND)](
    _,
    getters,
  ): {
    paths: string[];
    shapes: string[];
    textboxes: string[];
  } {
    const pathsKey = defineVuexName(MODULES.ANNOTATION_V2.GETTERS.GET_PATHS);
    const shapesKey = defineVuexName(MODULES.ANNOTATION_V2.GETTERS.GET_SHAPES);
    const textboxesKey = defineVuexName(MODULES.ANNOTATION_V2.GETTERS.GET_TEXTBOXES);
    return {
      paths: getters[pathsKey].map((obj: AnnotationObject) => obj.metadata),
      shapes: getters[shapesKey].map((obj: AnnotationObject) => obj.metadata),
      textboxes: getters[textboxesKey].map((obj: AnnotationObject) => obj.metadata),
    };
  },

  // ==================== REAL-TIME OBJECT UPDATES ====================

  /**
   * Get next object to be updated (for real-time incremental canvas updates)
   */
  [defineVuexName(MODULES.ANNOTATION_V2.GETTERS.GET_NEXT_OBJECT_TOBE_UPDATED)](state: AnnotationStateV2): CanvasObjectUpdate | undefined {
    return state.nextObjectTobeUpdated;
  },
};

export default getters;
